import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { BannerComponent } from '@components';
import { LetDirective } from '@ngrx/component';
import { GTMService } from '@services';
import {
  DashboardInstallationState,
  DashboardSelectedTab,
  DashboardStore,
  DashboardStoreStatusType,
  DashboardTileType,
} from '@store';
import { ViLoaderComponent } from '../../../../../projects/ui/components/loader/loader.component';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CommonStandaloneModule } from '../../../../shared/common-standalone.module';
import { DashboardService } from '../../dashboard.service';
import { DashboardTileBannersComponent } from '../dashboard-tile-banners/dashboard-tile-banners.component';
import { DashboardTileInstallationListItemComponent } from './dashboard-installation-list-item/dashboard-tile-installation-list-item.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    forwardRef(() => CommonStandaloneModule),
    LetDirective,
    DashboardTileInstallationListItemComponent,
    ViLoaderComponent,
    BannerComponent,
    DashboardTileBannersComponent,
  ],
  selector: 'app-dashboard-tile-installation-list',
  standalone: true,
  styleUrls: ['./dashboard-tile-installation-list.component.scss'],
  templateUrl: './dashboard-tile-installation-list.component.html',
})
export class DashboardTileInstallationListComponent implements OnInit, OnDestroy {
  dashboardInstallationState$: Observable<DashboardInstallationState>;
  dashboardStoreStatus$: Observable<DashboardStoreStatusType>;
  activeTileTab: string = null;

  @Input() installationLimit = 0;
  @Input() virtualScrollEnabled = false;
  @Input() tileType: DashboardTileType;
  @Output() navigateEvent: EventEmitter<void> = new EventEmitter<void>();
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private dashboardStore: DashboardStore,
    private router: Router,
    private gtmService: GTMService
  ) {}

  ngOnInit(): void {
    this.initActiveTileTab();
    this.dashboardInstallationState$ = this.dashboardStore.getInstallationsState(
      this.tileType,
      this.activeTileTab
    );
    this.dashboardStoreStatus$ = this.dashboardStore.getStoreStatus(
      this.tileType,
      this.activeTileTab
    );

    if (this.installationLimit > 0) {
      this.dashboardInstallationState$ = this.dashboardInstallationState$.pipe(
        map((installations: DashboardInstallationState) => {
          const limitedInstallations = installations.value.slice(0, this.installationLimit);

          return { ...installations, value: limitedInstallations };
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  goToInstallationDetails(installationId: number): void {
    this.navigateEvent.emit();
    setTimeout(() => {
      this.router.navigate(['installations/' + installationId]);
      this.gtmService.track('onGoToPage', 'Installations', 'dashboard');
    });
  }

  onScroll(event: Event, canFetch: boolean, fetching: boolean): void {
    if (!canFetch || fetching || !this.virtualScrollEnabled) {
      return;
    }

    const eventTarget = event.target as HTMLElement;
    const visibleHeight = eventTarget.offsetHeight;
    const scrolledFromTop = eventTarget.scrollTop;
    const totalHeight = eventTarget.scrollHeight;
    const offset = 88;

    if (Math.ceil(visibleHeight + scrolledFromTop) >= totalHeight - offset) {
      if (this.tileType === DashboardTileType.ContractsEndingSoon) {
        this.dashboardStore.fetchContractsEndingSoon(25);
      } else {
        this.dashboardStore.fetchInstallationsForSingleTileType(this.tileType, this.activeTileTab);
      }
    }
  }

  private initActiveTileTab(): void {
    if (this.dashboardService.dashboardTileHasTabs(this.tileType)) {
      this.dashboardService
        .getActiveTabForTile(this.tileType)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((tab: DashboardSelectedTab) => {
          this.activeTileTab = tab.selectedTabValue;
        });
    }
  }
}
